import type { ReactNode } from "react";
import { useRouterState } from "@tanstack/react-router";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";

/**
 * Cross-page transition.
 *
 * The wrapper only animates opacity — never filter/transform — because the
 * site nav is `position: fixed` and any filter/transform on an ancestor would
 * turn this element into its containing block.
 *
 * The staggered blur-rise of the page content is done in CSS
 * (`.page-transition` rules in styles.css) with `animation-fill-mode: backwards`
 * so no filter lingers on the element once the animation finishes.
 */
export function PageTransition({ children }: { children: ReactNode }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const reduceMotion = useReducedMotion();

  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={pathname}
        className={reduceMotion ? undefined : "page-transition"}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{
          duration: reduceMotion ? 0 : 0.22,
          ease: [0.22, 1, 0.36, 1],
        }}
      >
        {children}
      </motion.div>
    </AnimatePresence>
  );
}
